import type { BaseEntity } from "@/components/shared/UniversalCRMTable/types";
import type { DetailActionStrict, ActionVariant } from "../../types/detail-action";
import { ACTION_ICONS, ACTION_VARIANTS, ROLE_COMBINATIONS } from "../../types/action-properties";

export function createDuplicateTaskAction<T extends BaseEntity>(
  options: {
    label?: string;
    icon?: string;
    variant?: ActionVariant;
    allowedRoles?: string[];
  } = {}
): DetailActionStrict<T> {
  return {
    id: "duplicate-task",
    label: options.label ?? "Duplicate",
    icon: options.icon ?? ACTION_ICONS.DUPLICATE,
    variant: options.variant ?? ACTION_VARIANTS.OUTLINE,
    action: "duplicateTask",
    allowedRoles: options.allowedRoles ?? [...ROLE_COMBINATIONS.ADMIN_ONLY],
  };
}

export function createQuickCompleteAction<T extends BaseEntity>(
  options: {
    label?: string;
    icon?: string;
    variant?: ActionVariant;
    allowedRoles?: string[];
    primary?: boolean;
  } = {}
): DetailActionStrict<T> {
  return {
    id: "quick-complete",
    label: options.label ?? "Quick Complete",
    icon: options.icon ?? ACTION_ICONS.CHECK_CIRCLE,
    variant: options.variant ?? ACTION_VARIANTS.OUTLINE,
    action: "completeTask",
    allowedRoles: options.allowedRoles ?? [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
    primary: options.primary ?? false,
  };
}

export function createCompleteTaskAction<T extends BaseEntity>(
  options: {
    label?: string;
    icon?: string;
    variant?: ActionVariant;
    allowedRoles?: string[];
    bulkable?: boolean;
  } = {}
): DetailActionStrict<T> {
  return {
    id: "complete-task",
    label: options.label ?? "Complete",
    icon: options.icon ?? ACTION_ICONS.CHECK_CIRCLE,
    variant: options.variant ?? ACTION_VARIANTS.OUTLINE,
    action: "completeTask",
    allowedRoles: options.allowedRoles ?? [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
    bulkable: options.bulkable ?? true,
  };
}

export function createSetDueDateAction<T extends BaseEntity>(
  options: {
    label?: string;
    icon?: string;
    variant?: ActionVariant;
    allowedRoles?: string[];
    bulkable?: boolean;
  } = {}
): DetailActionStrict<T> {
  return {
    id: "set-due-date",
    label: options.label ?? "Adjust due date",
    icon: options.icon ?? ACTION_ICONS.CALENDAR,
    variant: options.variant ?? ACTION_VARIANTS.OUTLINE,
    action: "setDueDate",
    allowedRoles: options.allowedRoles ?? [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
    bulkable: options.bulkable ?? false,
  };
}

export function createAssignTaskToMeAction<T extends BaseEntity>(
  options: {
    label?: string;
    icon?: string;
    variant?: ActionVariant;
    allowedRoles?: string[];
    bulkable?: boolean;
  } = {}
): DetailActionStrict<T> {
  return {
    id: "assign-task-to-me",
    label: options.label ?? "Assign to me",
    icon: options.icon ?? ACTION_ICONS.USER,
    variant: options.variant ?? ACTION_VARIANTS.OUTLINE,
    action: "assignTaskToMe",
    allowedRoles: options.allowedRoles ?? [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
    bulkable: options.bulkable ?? true,
    tooltip: "Add yourself to this task's assignees.",
  };
}

export function createAddTaskNoteAction<T extends BaseEntity>(
  options: {
    label?: string;
    icon?: string;
    variant?: ActionVariant;
    allowedRoles?: string[];
  } = {}
): DetailActionStrict<T> {
  return {
    id: "add-task-note",
    label: options.label ?? "Add note",
    icon: options.icon ?? ACTION_ICONS.MESSAGE,
    variant: options.variant ?? ACTION_VARIANTS.OUTLINE,
    action: "addTaskNote",
    allowedRoles: options.allowedRoles ?? [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
  };
}
